import React, {useEffect, useState} from "react";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import moment from "moment";

import Loading from "@/components/Loading";
import Page from "@/components/Page";
import Breadcrumbs from "@/components/Breadcrumbs";
import EmptyData from "@/components/EmptyData";
import MainActions from "@/components/MainActions";
import VLFCard from "@/components/Card";
import ViewField from "@/components/Input/ViewField";
import VLFModal from "@/components/Modal";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

import {PlayerValuationAPI} from "@/api/player_valuation";
import SmartRadarChart from "@/components/SmartRadarChart";
import {
	apiFieldsToValues,
	SMART_PARAMS,
	SMART_CATEGORIES,
} from "@/config/smartValuationConfig";

import {
	ERROR_OPERATION,
	SUCCESSFUL_OPERATION,
	baseExternMatchParameters,
	baseExternTrainingParameters,
	dateFormat,
	initialBaseValuationParameters,
	initialValuationParameters,
	proExternMatchParameters,
	proExternTrainingParameters,
} from "@/utils/constants";
import {
	getAlertSeverity,
	getValuationType,
	getValuationScore,
} from "@/utils/functions";
import {useCategories} from "@/utils/hooks";

import "./index.scss";
import {useTranslation} from "react-i18next";

function Valuation(props) {
	const {handleNotification, external} = props;

	const playerId = new URLSearchParams(useLocation().search).get("playerId");

	const {id} = useParams();
	const navigate = useNavigate();
	const {t} = useTranslation();

	const {categories} = useCategories({
		setLoading: false,
		handleNotification: undefined,
		formatted: true,
	});

	const [loading, setLoading] = useState(false);
	const [openHandleConfirm, setOpenHandleConfirm] = useState(false);
	const [valuationData, setValuationData] = useState({});

	const getValuation = async () => {
		setLoading(true);
		try {
			const response = external
				? await PlayerValuationAPI.getById(id)
				: await PlayerValuationAPI.getById(id);
			setValuationData(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};
	useEffect(() => {
		getValuation();
	}, []);

	const deleteValuation = async () => {
		setLoading(true);
		try {
			external
				? await PlayerValuationAPI.delete(valuationData?.id)
				: await PlayerValuationAPI.delete(valuationData?.id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			navigate(external ? "/scouting/valuations" : "/players/" + playerId);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		} finally {
			setOpenHandleConfirm(false);
		}
	};

	const reportConfigurationSize = "col-6 col-xl-4";
	const reportConfiguration = [
		{
			value: valuationData?.valuationDate
				? moment(valuationData?.valuationDate).format(dateFormat)
				: null,
			label: "Data",
			size: reportConfigurationSize,
		},
		{
			value: valuationData?.valuationAuthor,
			label: "Allenatore",
			size: reportConfigurationSize,
		},
	];

	const currentPlayerData = valuationData?.player;
	if (external) {
		reportConfiguration.push(
			{
				value: getValuationType(valuationData.valuationType),
				label: "Tipologia",
				size: reportConfigurationSize,
			},
			{
				value: valuationData?.club?.name,
				label: "Squadra",
				size: reportConfigurationSize,
			},
		);

		reportConfiguration.push({
			value: valuationData?.category?.name,
			label: "Categoria",
			size: reportConfigurationSize,
		});
	} else {
		reportConfiguration.push({
			value: valuationData?.momentOfGrowth,
			label: "Momento di crescita",
			size: reportConfigurationSize,
		});
	}

	const playerReportConfigurationSize = "col-6 col-xl-3";
	const playerReportConfiguration = [
		{
			value: currentPlayerData
				? currentPlayerData?.firstName + " " + currentPlayerData?.lastName
				: "---",
			label: "Giocatore",
			size: playerReportConfigurationSize,
		},
		{
			value: !external
				? currentPlayerData?.category
				: currentPlayerData?.dateOfBirth
					? moment(currentPlayerData?.dateOfBirth).format(dateFormat)
					: "---",
			label: !external ? "Categoria" : "Data di nascita",
			size: playerReportConfigurationSize,
		},
		{
			value: currentPlayerData?.goalkeeper
				? "Portiere"
				: currentPlayerData?.primaryRole,
			label: "Ruolo",
			size: playerReportConfigurationSize,
		},
		{
			value: valuationData?.height ? valuationData?.height + " cm" : "",
			label: "Altezza",
			size: playerReportConfigurationSize,
		},
		{
			value: valuationData?.weight ? valuationData?.weight + " kg" : "",
			label: "Peso",
			size: playerReportConfigurationSize,
		},
	];

	if (external) {
		// playerReportConfiguration.push({
		// 	value: valuationData?.trialPeriod,
		// 	label: "Periodo di prova",
		// 	size: playerReportConfigurationSize,
		// });
	} else {
		playerReportConfiguration.push({
			value: valuationData?.timeInsideClub,
			label: "Nel club dal",
			size: playerReportConfigurationSize,
		});
	}

	const getCategoryNumber = (category) => {
		return parseInt(category?.split(" ")?.[1]);
	};

	const getParametersList = () => {
		// const playerCategory = categories?.filter(
		// 	(c) => c.id === currentPlayerData?.categoryId,
		// )?.[0];
		if (valuationData?.category?.basicActivities) {
			return initialBaseValuationParameters(t);
		} else {
			return initialValuationParameters(t)?.filter(
				(p) =>
					!p.roles ||
					p.roles.indexOf(currentPlayerData?.primaryRole?.toLowerCase()) !== -1,
			);
		}
	};

	let parameters = [];
	if (external) {
		if (valuationData?.valuationType && valuationData?.category) {
			if (!valuationData.category?.basicActivities) {
				if (valuationData.valuationType === 2) {
					parameters = proExternMatchParameters(t);
				}
				if (valuationData.valuationType === 3) {
					parameters = proExternTrainingParameters(t);
				}
			} else {
				if (valuationData.valuationType === 4) {
					parameters = baseExternMatchParameters(t);
				}
				if (valuationData.valuationType === 5) {
					parameters = baseExternTrainingParameters(t);
				}
			}
		}
	} else {
		// Simplified internal valuation: no parameter grid
		parameters = [];
	}

	const valuationTextItem = {
		value: (
			<div className="area-rating-container only-text">
				{valuationData?.valuationText ? (
					<div className="rating-notes big">{valuationData?.valuationText}</div>
				) : (
					<EmptyData />
				)}
			</div>
		),
		label: external ? "Relazione generale" : "Valutazione iniziale",
		size: "col-12",
	};

	const reportDetailsConfiguration = parameters.map((p) => {
		return {
			value: (
				<div className="area-rating-container">
					{valuationData[p.fieldKeyRating] && (
						<div className="stat-circle-container">
							<div className="stat-container">
								<div className="value">
									{+parseFloat(valuationData[p.fieldKeyRating]).toFixed(2)}
								</div>
							</div>
						</div>
					)}
					{valuationData?.[p.fieldKey] && (
						<div className="rating-notes">{valuationData?.[p.fieldKey]}</div>
					)}
					{!valuationData?.[p.fieldKeyRating] &&
						!valuationData?.[p.fieldKey] && <EmptyData />}
				</div>
			),
			label: p.label,
			size: "col-12 col-sm-6",
		};
	});

	// Internal: valuationText (main content) goes first
	if (!external) {
		reportDetailsConfiguration.push(valuationTextItem);
	}
	if (!external) {
		reportDetailsConfiguration.push({
			value: (
				<div className="area-rating-container only-text">
					{valuationData?.strengthAreas ? (
						<div className="rating-notes">{valuationData?.strengthAreas}</div>
					) : (
						<EmptyData />
					)}
				</div>
			),
			label: "Punti di forza, aree migliorabili",
			size: "col-12 col-sm-6",
		});
	}
	if (valuationData.valuationType !== 6) {
		reportDetailsConfiguration.push({
			value: (
				<div className="area-rating-container only-text">
					{valuationData?.finalOverallValuation ? (
						<div className="rating-notes">
							{valuationData?.finalOverallValuation}
						</div>
					) : (
						<EmptyData />
					)}
				</div>
			),
			label: "Valutazione complessiva finale",
			size: "col-12 col-sm-6",
		});
	}
	// External: valuationText goes last
	if (external) {
		reportDetailsConfiguration.push(valuationTextItem);
	}

	const handleCopyLink = () => {
		const url =
			window.location.origin + "/public/valuation/" + valuationData?.id;
		navigator.clipboard
			.writeText(url)
			.then(() => handleNotification("Link copiato negli appunti", "success"))
			.catch(() => handleNotification("Impossibile copiare il link", "error"));
	};

	const mainActions = [
		{
			icon: <ArrowBackIcon />,
			title: "Indietro",
			onClick: () => navigate(-1),
			color: "info",
		},
		...(external
			? [
					{
						icon: <ContentCopyIcon />,
						title: "Copia link",
						onClick: handleCopyLink,
						color: "info",
					},
				]
			: []),
		{
			icon: <EditIcon />,
			title: "Modifica",
			onClick: () =>
				navigate(
					external
						? "/scouting/valuations/" + valuationData?.id + "/edit"
						: "/players-valuations/" +
								valuationData?.id +
								"/edit?playerId=" +
								playerId,
				),
		},
		{
			icon: <DeleteIcon />,
			title: "Elimina",
			onClick: () => setOpenHandleConfirm(true),
			color: "error",
		},
	];

	const breadcrumbsElements = [
		{
			label: "Valutazioni",
			path: external ? "/scouting/valuations" : "/players/" + playerId,
		},
		{label: "/"},
	];

	return (
		<Page className="page-container">
			<Loading visible={loading} />
			<Breadcrumbs
				elements={breadcrumbsElements}
				current={
					valuationData
						? moment(valuationData?.valuationDate).format(dateFormat) +
							" " +
							(currentPlayerData?.firstName || "") +
							" " +
							(currentPlayerData?.lastName || "")
						: ""
				}
				actions={<MainActions actions={mainActions} />}
			/>
			<div className="sheet-container">
				{/* ── SMART Valuation Layout ──────────────────── */}
				{valuationData?.valuationType === 7 ? (
					<>
						<div className="hv-player-banner">
							<div className="hv-player-banner__name">
								{currentPlayerData
									? `${currentPlayerData.firstName} ${currentPlayerData.lastName}`
									: "—"}
							</div>
							<div className="hv-player-banner__meta">
								<span>{currentPlayerData?.primaryRole || ""}</span>
								<span className="hv-player-banner__dot">·</span>
								<span>{currentPlayerData?.category || "—"}</span>
							</div>
						</div>
						<div className="vd-meta-strip">
							<div className="vd-meta-strip__field">
								<span className="vd-meta-strip__label">Data</span>
								<span className="vd-meta-strip__value">
									{valuationData?.valuationDate
										? moment(valuationData.valuationDate).format(dateFormat)
										: "—"}
								</span>
							</div>
							<div className="vd-meta-strip__field">
								<span className="vd-meta-strip__label">Autore</span>
								<span className="vd-meta-strip__value">
									{valuationData?.valuationAuthor || "—"}
								</span>
							</div>
							<div className="vd-meta-strip__field">
								<span className="vd-meta-strip__label">Tipo</span>
								<span className="vd-meta-strip__value">Valutazione SMART</span>
							</div>
						</div>
						<div className="row no-margin">
							<div className="col-12 col-lg-6 padding-small">
								<VLFCard
									title="Profilo SMART"
									openable={false}
									content={
										<SmartRadarChart
											values={apiFieldsToValues(valuationData)}
											playerName={currentPlayerData ? `${currentPlayerData.firstName} ${currentPlayerData.lastName}` : ""}
										/>
									}
								/>
							</div>
							<div className="col-12 col-lg-6 padding-small">
								<VLFCard
									title="Attributi"
									openable={false}
									content={
										<div style={{display: "flex", flexDirection: "column", gap: 12}}>
											{SMART_CATEGORIES.map((cat) => {
												const params = SMART_PARAMS.filter((p) => p.category === cat.id);
												return (
													<div key={cat.id}>
														<div style={{fontSize: 10, fontWeight: 700, color: cat.color, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6}}>
															{cat.label}
														</div>
														{params.map((param) => {
															const val = apiFieldsToValues(valuationData)[param.id];
															return (
																<div key={param.id} style={{display: "flex", alignItems: "center", gap: 8, marginBottom: 4}}>
																	<span style={{fontSize: 12, color: "var(--color-text)", fontWeight: 500, flex: 1}}>{param.label}</span>
																	<span style={{fontSize: 13, fontWeight: 800, color: cat.color, minWidth: 28, textAlign: "right"}}>
																		{val ?? "—"}
																	</span>
																	<div style={{width: 80, height: 5, background: "var(--color-border)", borderRadius: 999, overflow: "hidden"}}>
																		<div style={{height: "100%", width: `${val || 0}%`, background: cat.color, borderRadius: 999}} />
																	</div>
																</div>
															);
														})}
													</div>
												);
											})}
										</div>
									}
								/>
							</div>
						</div>
						{valuationData?.valuationText && (
							<div className="row no-margin">
								<div className="col-12 padding-small">
									<VLFCard
										title="Note"
										openable={false}
										content={<p style={{fontSize: 13, color: "var(--color-text)", margin: 0}}>{valuationData.valuationText}</p>}
									/>
								</div>
							</div>
						)}
					</>
				) : !external ? (
					<>
						{/* Player banner */}
						<div className="hv-player-banner">
							<div className="hv-player-banner__name">
								{currentPlayerData
									? `${currentPlayerData.firstName} ${currentPlayerData.lastName}`
									: "—"}
							</div>
							<div className="hv-player-banner__meta">
								<span>
									{currentPlayerData?.goalkeeper
										? "Portiere"
										: currentPlayerData?.primaryRole || "—"}
								</span>
								<span className="hv-player-banner__dot">·</span>
								<span>{currentPlayerData?.category || "—"}</span>
							</div>
						</div>

						{/* Meta strip */}
						<div className="vd-meta-strip">
							<div className="vd-meta-strip__field">
								<span className="vd-meta-strip__label">Data</span>
								<span className="vd-meta-strip__value">
									{valuationData?.valuationDate
										? moment(valuationData.valuationDate).format(dateFormat)
										: "—"}
								</span>
							</div>
							<div className="vd-meta-strip__field">
								<span className="vd-meta-strip__label">Allenatore</span>
								<span className="vd-meta-strip__value">
									{valuationData?.valuationAuthor || "—"}
								</span>
							</div>
							{valuationData?.height ? (
								<div className="vd-meta-strip__field">
									<span className="vd-meta-strip__label">Altezza</span>
									<span className="vd-meta-strip__value">
										{valuationData.height} cm
									</span>
								</div>
							) : null}
							{valuationData?.weight ? (
								<div className="vd-meta-strip__field">
									<span className="vd-meta-strip__label">Peso</span>
									<span className="vd-meta-strip__value">
										{valuationData.weight} kg
									</span>
								</div>
							) : null}
							{valuationData?.momentOfGrowth ? (
								<div className="vd-meta-strip__field">
									<span className="vd-meta-strip__label">
										Momento di crescita
									</span>
									<span className="vd-meta-strip__value">
										{valuationData.momentOfGrowth}
									</span>
								</div>
							) : null}
							{[1, 6].includes(valuationData?.valuationType) ? (
								<div className="vd-meta-strip__field vd-meta-strip__field--score">
									<span className="vd-meta-strip__label">Punteggio</span>
									<span className="vd-meta-strip__score">
										{getValuationScore(valuationData) ?? "---"}
									</span>
								</div>
							) : null}
						</div>
					</>
				) : (
					<>
						<div className="row no-margin col-container">
							<div className="col-sm-12 col-lg-12 padding-small">
								<VLFCard
									title="Informazioni valutazione"
									content={
										<div className="row">
											<div className="col-sm-12 col-lg-10">
												<ViewField configuration={reportConfiguration} />
											</div>
											<div className="col-sm-12 col-lg-2">
												<div
													className="d-flex flex-column align-items-center gap-2 h-100"
													style={{borderLeft: "1px solid var(--color-border)"}}
												>
													{
														<div className="view-field-container avg h-100 justify-content-center">
															<div className="field-label-container justify-content-center">
																<div className="field-label">
																	{valuationData.valuationType === 6
																		? "Punteggio"
																		: "Voto finale"}
																</div>
															</div>
															<div className="stat-circle-container">
																<div className="stat-container">
																	<div className="value">
																		{getValuationScore(valuationData) == null
																			? "---"
																			: +getValuationScore(
																					valuationData,
																				).toFixed(2)}
																	</div>
																</div>
															</div>
														</div>
													}
													<div className="view-field-container avg h-100 justify-content-center">
														<div className="field-label-container justify-content-center">
															<div className="field-label">Esito</div>
														</div>
														<div className="d-flex justify-content-center">
															<div
																style={{maxWidth: "150px"}}
																className={
																	"severity " +
																	(getAlertSeverity(
																		valuationData?.finalOverallRating,
																		true,
																	)?.severity || "")
																}
															>
																{
																	getAlertSeverity(
																		valuationData?.finalOverallRating,
																		true,
																	)?.message
																}
															</div>
														</div>
													</div>
												</div>
											</div>
										</div>
									}
									openable={false}
								/>
							</div>
						</div>
						<div className="row no-margin mt-2">
							<div className="col-sm-12 padding-small">
								<VLFCard
									title="Informazioni giocatore"
									content={
										<div className="row">
											<div className="col-sm-12">
												<ViewField configuration={playerReportConfiguration} />
											</div>
										</div>
									}
								/>
							</div>
						</div>
					</>
				)}

				{/* Dettagli — shared */}
				<div className="row no-margin mt-2">
					<div className="col-sm-12 padding-small">
						<VLFCard
							title="Dettagli"
							content={
								<div className="row">
									<div className="col-sm-12">
										<ViewField configuration={reportDetailsConfiguration} />
									</div>
								</div>
							}
						/>
					</div>
				</div>
			</div>
			<VLFModal
				open={openHandleConfirm}
				title="Eliminazione"
				content={
					<div className="mt-2">
						Sei sicuro di voler eliminare questo elemento?
					</div>
				}
				confirm={() => deleteValuation()}
				close={() => setOpenHandleConfirm(false)}
				confirmLabel="Conferma"
			/>
		</Page>
	);
}

export default Valuation;
